import React, { useEffect, useState, useCallback } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  Upload,
  message,
  Row,
  Col,
  Select,
  DatePicker,
  Switch,
  Spin,
} from "antd";
import {
  UploadOutlined,
  UserOutlined,
  MailOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

import { PatientReviApi } from "../../../../../api/revi/patientApi";
import { useAuth } from "../../../../../hooks";

const api = new PatientReviApi();
const { Option } = Select;

export function PatientFormModal({
  visible,
  onClose,
  onCancel, // soportamos ambos
  patient,
  institutionId,
  therapistsList = [],
  onSuccess,
}) {
  const [form] = Form.useForm();
  const { t } = useTranslation(["institution", "common"]);
  const { accessToken } = useAuth();

  const [saving, setSaving] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [fileList, setFileList] = useState([]);

  const patientId = patient?._id ?? null;

  const safeClose = useCallback(() => {
    if (typeof onClose === "function") return onClose();
    if (typeof onCancel === "function") return onCancel();
    // no-op para no romper si el padre no pasó handler
  }, [onClose, onCancel]);

  const buildRemoteFileList = useCallback((url) => {
    if (!url) return [];
    return [{ uid: "-1", name: "avatar", status: "done", url }];
  }, []);

  // Permitir reemplazar avatar aunque haya uno cargado
  const handleChangeUpload = ({ fileList: newFileList }) => {
    // Siempre nos quedamos con el último => reemplaza el avatar anterior
    setFileList(newFileList.slice(-1));
  };

  useEffect(() => {
    let cancelled = false;

    const loadProfile = async () => {
      if (!visible) return;

      // Limpieza fuerte (evita “memoria”)
      form.resetFields();
      setFileList([]);

      // Precarga rápida con lo que venga del listado (sin email!)
      if (patient) {
        const therapistIds = Array.isArray(patient.terapeutas)
          ? patient.terapeutas.map((t) => (typeof t === "object" ? t._id : t))
          : [];

        form.setFieldsValue({
          name: patient.name ?? "",
          surname: patient.surname ?? "",
          email: "", // CLAVE: nunca desde listado
          password: "",
          sexo: patient.sexo ?? undefined,
          birthdate: patient.birthdate ? dayjs(patient.birthdate) : null,
          therapistIds,
          active: typeof patient.active === "boolean" ? patient.active : true,
        });

        setFileList(buildRemoteFileList(patient.avatar));
      }

      // Si es nuevo, no hay profile
      if (!patientId) return;

      // Token válido
      if (typeof accessToken !== "string" || accessToken.trim().length < 10) {
        message.error(t("patients.form.invalidToken"));
        return;
      }

      setLoadingProfile(true);
      try {
        const { patient: fullPatient, user } = await api.getById(
          accessToken,
          patientId,
        );
        if (cancelled) return;

        const therapistIds = Array.isArray(fullPatient?.terapeutas)
          ? fullPatient.terapeutas.map((t) =>
              typeof t === "object" ? t._id : t,
            )
          : [];

        const avatarUrl = user?.avatar ?? fullPatient?.avatar ?? null;

        form.setFieldsValue({
          name: fullPatient?.name ?? "",
          surname: fullPatient?.surname ?? "",
          email: (user?.email ?? "").trim(),
          password: "",
          sexo: fullPatient?.sexo ?? undefined,
          birthdate: fullPatient?.birthdate
            ? dayjs(fullPatient.birthdate)
            : null,
          therapistIds,
          active:
            typeof fullPatient?.active === "boolean"
              ? fullPatient.active
              : true,
        });

        setFileList(buildRemoteFileList(avatarUrl));
      } catch (e) {
        console.error(e);
        message.error(e.message || t("patients.form.loadError"));
      } finally {
        if (!cancelled) setLoadingProfile(false);
      }
    };

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [visible, patientId, patient, accessToken, form, buildRemoteFileList, t]);

  const handleSubmit = async (values) => {
    try {
      setSaving(true);

      const formData = new FormData();
      formData.append("name", values.name);
      formData.append("surname", values.surname);
      formData.append("email", values.email);
      formData.append("sexo", values.sexo || "");
      formData.append(
        "active",
        values.active !== undefined ? values.active : true,
      );

      if (values.birthdate) {
        formData.append("birthdate", values.birthdate.format("DD-MM-YYYY"));
      }

      if (values.password) {
        formData.append("password", values.password);
      }

      formData.append(
        "therapistIds",
        JSON.stringify(values.therapistIds || []),
      );

      // Avatar solo si es archivo nuevo
      const file = fileList?.[0]?.originFileObj;
      if (file) formData.append("avatar", file);

      if (patientId) {
        await api.update(accessToken, patientId, formData);
        message.success(t("patients.form.updated"));
      } else {
        await api.create(accessToken, institutionId, formData);
        message.success(t("patients.form.created"));
      }

      if (typeof onSuccess === "function") onSuccess();
      safeClose();
    } catch (error) {
      console.error(error);
      message.error(error.message || t("patients.form.saveError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={
        patientId ? t("patients.form.titleEdit") : t("patients.form.titleNew")
      }
      open={visible}
      onCancel={safeClose}
      footer={null}
      destroyOnHidden
      width={700}
    >
      <Spin spinning={loadingProfile}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label={t("common:fields.name")}
                rules={[{ required: true }]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder={t("common:fields.name")}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="surname"
                label={t("common:fields.surname")}
                rules={[{ required: true }]}
              >
                <Input placeholder={t("common:fields.surname")} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="email"
            label={t("patients.form.emailLabel")}
            rules={[{ required: true, type: "email" }]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder={t("patients.form.emailPlaceholder")}
            />
          </Form.Item>

          <Form.Item
            name="password"
            label={t("patients.form.passwordLabel")}
            rules={[{ min: 6, message: t("patients.form.passwordMin") }]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder={t("patients.form.passwordPlaceholder")}
            />
          </Form.Item>

          <Form.Item label={t("patients.form.avatar")}>
            <Upload
              beforeUpload={() => false}
              fileList={fileList}
              onChange={handleChangeUpload}
              // Truco: maxCount=2 para que NO se bloquee el selector cuando ya hay 1 item,
              // pero como nosotros hacemos slice(-1), nunca habrá más de 1 realmente.
              maxCount={2}
              listType="picture"
              accept="image/*"
              showUploadList={{
                showPreviewIcon: true,
                showRemoveIcon: true,
              }}
            >
              <Button icon={<UploadOutlined />}>
                {fileList.length
                  ? t("patients.form.avatarChange")
                  : t("patients.form.avatarUpload")}
              </Button>
            </Upload>
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="birthdate" label={t("patients.form.birthdate")}>
                <DatePicker style={{ width: "100%" }} format="DD-MM-YYYY" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="sexo" label={t("patients.form.sex")}>
                <Select
                  allowClear
                  placeholder={t("patients.form.sexPlaceholder")}
                >
                  <Option value="M">{t("patients.form.sexMale")}</Option>
                  <Option value="F">{t("patients.form.sexFemale")}</Option>
                  <Option value="O">{t("patients.form.sexOther")}</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="therapistIds"
            label={t("patients.form.assignTherapists")}
          >
            <Select
              mode="multiple"
              placeholder={t("patients.form.assignTherapistsPlaceholder")}
              showSearch={{
                optionFilterProp: "label",
                filterOption: (input, option) =>
                  String(option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase()),
              }}
              options={therapistsList
                .filter((t) => t.active !== false)
                .map((t) => ({
                  value: t._id,
                  label: `${t.firstname} ${t.lastname}`,
                }))}
            />
          </Form.Item>

          <Form.Item
            name="active"
            label={t("patients.form.active")}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Button
            type="primary"
            htmlType="submit"
            loading={saving}
            block
            disabled={loadingProfile}
          >
            {patientId
              ? t("common:actions.update")
              : t("common:actions.create")}
          </Button>
        </Form>
      </Spin>
    </Modal>
  );
}
